import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Users, Baby, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { timetable, CLASS_CAPACITY } from '../mock/mock';
import BookingDialog from '../components/BookingDialog';
import { fetchBookingCounts } from '../services/api';

export default function TimetablePage() {
  const [counts, setCounts] = useState({});
  const [openBook, setOpenBook] = useState(false);
  const [selected, setSelected] = useState(null);

  const refresh = () => fetchBookingCounts().then(setCounts).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const openBooking = (slot, day) => {
    setSelected({ ...slot, day });
    setOpenBook(true);
  };

  return (
    <main data-testid="timetable-page">
      {/* Hero — flyer-style headline */}
      <section className="relative overflow-hidden bg-black">
        <div className="absolute -left-40 -top-40 w-[600px] h-[600px] rounded-full brand-gradient opacity-15 blur-3xl" />
        <div className="absolute -right-40 -bottom-40 w-[600px] h-[600px] rounded-full brand-gradient opacity-15 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-28 text-center">
          <div className="text-[11px] tracking-[0.35em] uppercase text-brand font-bold">ST Fitness, Irlam</div>
          <h1 className="mt-5 font-display italic text-brand uppercase text-6xl md:text-8xl leading-[0.9]">
            Group Sessions
          </h1>
          <h2 className="mt-3 font-display italic text-outline uppercase text-5xl md:text-7xl leading-[0.9]">
            Weekly Timetable
          </h2>
          <div className="mt-6 mx-auto w-64 h-2 rounded-full brand-gradient opacity-90" />
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.3em] text-white/70">
            <span className="text-brand font-bold flex items-center gap-2"><Users className="w-3.5 h-3.5" /> Small Groups</span>
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <span className="text-brand font-bold flex items-center gap-2"><Baby className="w-3.5 h-3.5" /> Kids Welcome</span>
          </div>
        </div>
      </section>

      {/* Timetable grid */}
      <section className="py-16 md:py-20 bg-[#0a0a0d] border-t border-white/5">
        <div className="max-w-5xl mx-auto px-5 md:px-8 space-y-4">
          {timetable.map((d) => (
            <Card key={d.day} data-testid={`day-${d.day.toLowerCase()}`} className="bg-transparent border-0 rounded-none">
              <div className="grid md:grid-cols-4 gap-3 md:gap-6 items-stretch">
                <div className="md:col-span-1 bg-brand text-black p-5 md:p-6 flex items-center">
                  <div className="font-display uppercase text-2xl md:text-3xl leading-none">{d.day}</div>
                </div>
                <div className="md:col-span-3 divide-y divide-white/10 border-b border-white/10">
                  {d.slots.map((s, i) => {
                    const capacity = s.capacity ?? CLASS_CAPACITY;
                    const booked = counts[s.slotId] || 0;
                    const remaining = capacity - booked;
                    const full = remaining <= 0;
                    const isPrivate = !!s.private;
                    const locked = isPrivate || full;
                    return (
                      <button
                        key={i}
                        onClick={() => !locked && openBooking(s, d.day)}
                        disabled={locked}
                        data-testid={`slot-${s.slotId}`}
                        className={`w-full py-5 flex items-center justify-between text-left group ${locked ? 'opacity-70 cursor-not-allowed' : 'hover:bg-white/[0.03]'}`}
                      >
                        <div className="flex items-center gap-6 md:gap-10 flex-1 min-w-0">
                          <div className="font-display text-2xl md:text-3xl text-white leading-none min-w-[100px]">{s.time}</div>
                          <div className="min-w-0">
                            <div className="text-white text-lg md:text-xl font-semibold truncate group-hover:text-brand transition-colors">{s.name}</div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mt-1">
                              {s.level}
                              <span className="mx-2 opacity-40">·</span>
                              {isPrivate ? (
                                <span className="text-brand">{s.note || 'Private block booking only'}</span>
                              ) : (
                                <span className={`${remaining <= 3 && !full ? 'text-brand' : ''}`}>
                                  {full ? 'Class Full' : `${remaining} of ${capacity} spots left`}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 pl-3">
                          <Badge className={`rounded-none uppercase text-[10px] tracking-widest border-0 ${locked ? 'bg-white/10 text-white/60' : 'brand-gradient text-white'}`}>
                            {isPrivate ? 'Private' : (full ? 'Full' : 'Message to book')}
                          </Badge>
                          {!locked && <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-brand transition-colors" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA banner */}
      <section className="py-14 bg-black border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center px-5">
          <p className="text-white/70 text-sm">Tap any slot above to book your spot. First class always sits right, ask questions before, during or after.</p>
          <Link to="/contact" className="mt-4 inline-flex items-center gap-2 text-brand hover:underline text-[11px] uppercase tracking-[0.25em]">
            Prefer to message? Contact Sam <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <BookingDialog open={openBook} onOpenChange={setOpenBook} slot={selected} onBooked={refresh} />
    </main>
  );
}
